import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { 
  ZoomIn, 
  ZoomOut, 
  ChevronLeft, 
  ChevronRight, 
  Download,
  Maximize2,
  Minimize2
} from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';

// Configure PDF.js worker using ES module that Vite can resolve
if (typeof window !== 'undefined') {
  // Create a worker from the package using import.meta.url that Vite rewrites
  const worker = new Worker(
    new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url),
    { type: "module" }
  );
  
  // Tell PDF.js to use this worker instance
  pdfjsLib.GlobalWorkerOptions.workerPort = worker;
}

interface PDFViewerProps {
  fileUrl: string;
  fileName?: string;
  className?: string;
}

export const PDFViewer: React.FC<PDFViewerProps> = ({ 
  fileUrl, 
  fileName = 'document.pdf',
  className = ''
}) => {
  const { toast } = useToast();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  
  const [pdf, setPdf] = useState<any>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [scale, setScale] = useState(1.0);
  const [isLoading, setIsLoading] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [renderTask, setRenderTask] = useState<any>(null);

  // Load PDF document
  useEffect(() => {
    const loadPDF = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        console.log('PDFViewer: Starting to load PDF:', fileUrl);
        console.log('PDFViewer: URL type:', fileUrl.startsWith('blob:') ? 'blob' : 'regular');
        
        // For blob URLs, we need to configure PDF.js differently
        const loadingConfig = fileUrl.startsWith('blob:') 
          ? { url: fileUrl, disableAutoFetch: true, disableStream: true }
          : fileUrl;
        
        console.log('PDFViewer: Loading config:', loadingConfig);
        
        const loadingTask = pdfjsLib.getDocument(loadingConfig);
        const pdfDoc = await loadingTask.promise;
        
        console.log('PDFViewer: PDF loaded successfully, pages:', pdfDoc.numPages);
        
        setPdf(pdfDoc);
        setTotalPages(pdfDoc.numPages);
        setCurrentPage(1);
        
        toast({
          title: "PDF loaded successfully",
          description: `${pdfDoc.numPages} pages loaded`,
        });
      } catch (err) {
        console.error('PDFViewer: Detailed error loading PDF:', err);
        console.error('PDFViewer: Error name:', err.name);
        console.error('PDFViewer: Error message:', err.message);
        console.error('PDFViewer: File URL:', fileUrl);
        
        setError(`Failed to load PDF: ${err.message}`);
        toast({
          title: "Error loading PDF",
          description: `${err.message || 'Unknown error'}`,
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    if (fileUrl) {
      console.log('PDFViewer: fileUrl changed, loading PDF:', fileUrl);
      loadPDF();
    }
  }, [fileUrl, toast]);

  // Render current page
  useEffect(() => {
    const renderPage = async () => {
      if (!pdf || !canvasRef.current) return;

      // Cancel any existing render task
      if (renderTask) {
        console.log('PDFViewer: Cancelling previous render task');
        renderTask.cancel();
        setRenderTask(null);
      }

      try {
        console.log('PDFViewer: Starting to render page', currentPage);
        
        const page = await pdf.getPage(currentPage);
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        if (!context) {
          console.error('PDFViewer: Failed to get canvas context');
          setError('Failed to get canvas context');
          return;
        }

        const viewport = page.getViewport({ scale });
        
        // Set canvas dimensions
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        
        // Clear canvas before rendering
        context.clearRect(0, 0, canvas.width, canvas.height);

        const renderContext = {
          canvasContext: context,
          viewport: viewport
        };

        console.log('PDFViewer: Rendering page with viewport:', viewport.width, 'x', viewport.height);
        
        // Start the render task and track it
        const newRenderTask = page.render(renderContext);
        setRenderTask(newRenderTask);
        
        await newRenderTask.promise;
        console.log('PDFViewer: Page rendered successfully');
        
        // Clear render task and error on success
        setRenderTask(null);
        setError(null);
        
      } catch (err) {
        // Don't log errors for cancelled operations
        if (err.name === 'RenderingCancelledException') {
          console.log('PDFViewer: Render cancelled (expected)');
          return;
        }
        
        console.error('PDFViewer: Error rendering page:', err);
        console.error('PDFViewer: Error details:', {
          name: err?.name,
          message: err?.message,
          stack: err?.stack
        });
        setError(`Failed to render page: ${err?.message || 'Unknown error'}`);
        setRenderTask(null);
      }
    };

    renderPage();
    
    // Cleanup function to cancel render task on unmount or dependencies change
    return () => {
      if (renderTask) {
        console.log('PDFViewer: Cleanup - cancelling render task');
        renderTask.cancel();
        setRenderTask(null);
      }
    };
  }, [pdf, currentPage, scale]);

  const handlePrevPage = () => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(totalPages, prev + 1));
  };

  const handleZoomIn = () => {
    setScale(prev => Math.min(3.0, prev + 0.25));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(0.5, prev - 0.25));
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleFullscreen = () => {
    setIsFullscreen(prev => !prev);
  };

  const handlePageInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const pageNum = parseInt(e.target.value);
    if (pageNum >= 1 && pageNum <= totalPages) {
      setCurrentPage(pageNum);
    }
  };

  if (error) {
    return (
      <Card className={`p-6 ${className}`}>
        <div className="text-center text-destructive">
          <p className="font-semibold">Error Loading PDF</p>
          <p className="text-sm mt-2">{error}</p>
        </div>
      </Card>
    );
  }

  return (
    <Card className={`${className} ${isFullscreen ? 'fixed inset-0 z-50 rounded-none' : ''}`}>
      {/* PDF Toolbar */}
      <div className="flex items-center justify-between p-4 border-b bg-muted/50">
        <div className="flex items-center gap-2">
          <Badge variant="outline">{fileName}</Badge>
          {totalPages > 0 && (
            <Badge variant="secondary">
              {totalPages} page{totalPages !== 1 ? 's' : ''}
            </Badge>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {/* Page Navigation */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handlePrevPage}
              disabled={currentPage <= 1 || isLoading}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            <div className="flex items-center gap-1 text-sm">
              <input
                type="number"
                value={currentPage}
                onChange={handlePageInput}
                className="w-12 px-1 py-1 text-center border rounded text-xs"
                min="1"
                max={totalPages}
                disabled={isLoading}
              />
              <span>of {totalPages}</span>
            </div>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleNextPage}
              disabled={currentPage >= totalPages || isLoading}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          {/* Zoom Controls */}
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              onClick={handleZoomOut}
              disabled={scale <= 0.5 || isLoading}
            >
              <ZoomOut className="w-4 h-4" />
            </Button>
            
            <span className="text-xs px-2 py-1 bg-background border rounded">
              {Math.round(scale * 100)}%
            </span>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleZoomIn}
              disabled={scale >= 3.0 || isLoading}
            >
              <ZoomIn className="w-4 h-4" />
            </Button>
          </div>

          {/* Action Buttons */}
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownload}
            disabled={isLoading}
          >
            <Download className="w-4 h-4" />
          </Button>
          
          <Button
            variant="outline"
            size="sm"
            onClick={toggleFullscreen}
          >
            {isFullscreen ? (
              <Minimize2 className="w-4 h-4" />
            ) : (
              <Maximize2 className="w-4 h-4" />
            )}
          </Button>
        </div>
      </div>

      {/* PDF Canvas Container */}
      <div 
        ref={containerRef}
        className={`relative overflow-auto bg-gray-100 ${
          isFullscreen ? 'flex-1' : 'h-[600px]'
        }`}
      >
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-sm text-muted-foreground">Loading PDF...</p>
            </div>
          </div>
        ) : (
          <div className="flex justify-center p-4">
            <canvas
              ref={canvasRef}
              className="border border-gray-300 shadow-lg bg-white"
              style={{ maxWidth: '100%', height: 'auto' }}
            />
          </div>
        )}
      </div>
    </Card>
  );
};